import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MediaListItem from "@/components/feature/MediaListItem";
import MediaCard from "@/components/feature/MediaCard";
import { mediaList } from "@/mocks/media";
import type { MediaItem } from "@/types/media";
import { searchMedia } from "@/utils/media";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") ?? "";
  const [input, setInput] = useState(keyword);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const all = mediaList as MediaItem[];
  const results = useMemo(() => (keyword ? searchMedia(all, keyword) : []), [all, keyword]);

  const hotPicks = useMemo(
    () => [...all].sort((a, b) => b.views - a.views).slice(0, 5),
    [all],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const kw = input.trim();
    setSearchParams(kw ? { keyword: kw } : {});
  };

  const clearKeyword = () => {
    setInput("");
    setSearchParams({});
    inputRef.current?.focus();
  };

  return (
    <PageLayout
      title={keyword ? `“${keyword}”搜索结果 - 桔子TV` : "搜索影视 - 桔子TV"}
      description={keyword ? `在桔子TV搜索“${keyword}”的相关影视作品，查看匹配的电视剧、电影与综艺内容。` : "在桔子TV搜索电视剧、电影与综艺，快速找到想看的影视作品。"}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 mb-5">搜索影视</h1>

        <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-3xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
            <input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              type="text"
              name="keyword"
              placeholder="输入影视名称、演员或类型，如：好好的时光"
              className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-300 text-sm md:text-base text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        {keyword && (
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className="text-sm text-foreground-600">当前搜索：</span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/15 text-primary-400 text-sm">
              {keyword}
              <button type="button" onClick={clearKeyword} aria-label="清除搜索词" className="cursor-pointer hover:text-primary-200 transition-colors">
                <i className="ri-close-line" />
              </button>
            </span>
            <span className="text-sm text-foreground-600">
              共找到 <strong className="text-foreground-950 font-semibold">{results.length}</strong> 个结果
            </span>
          </div>
        )}

        <div className="mt-7">
          {!keyword && (
            <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-search-eye-line text-3xl text-foreground-500" />
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mt-4">输入关键词开始搜索</h3>
              <p className="text-sm text-foreground-600 mt-2">支持影视名称、主演、导演、类型等多维度模糊搜索</p>
              <div className="mt-6">
                <p className="text-xs text-foreground-600 mb-3 font-label">大家都在搜</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {["好好的时光", "都市", "悬疑", "综艺", "古装"].map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => setSearchParams({ keyword: word })}
                      className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 hover:border-primary-400 text-sm text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {keyword && results.length > 0 && (
            <div className="space-y-3">
              {results.map((item, index) => (
                <MediaListItem key={item.id} item={item} index={index + 1} />
              ))}
            </div>
          )}

          {keyword && results.length === 0 && (
            <div>
              <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
                <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-emotion-unhappy-line text-3xl text-foreground-500" />
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mt-4">未找到与“{keyword}”相关的影视内容</h3>
                <p className="text-sm text-foreground-600 mt-2">换个关键词试试，或看看下面的热门推荐</p>
                <button
                  type="button"
                  onClick={clearKeyword}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  清除搜索
                </button>
              </div>

              <div className="mt-10">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  热门影视推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {hotPicks.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10">
          <Link to="/category" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
            <i className="ri-compass-3-line" />
            没有找到想要的？去分类页慢慢逛
          </Link>
        </div>
      </div>
    </PageLayout>
  );
}